import output from "_src/engine/output";
import events from "_src/engine/events";

import { colorUtils, idGenerator } from "_src/utils";
import {objectType} from "_src/constants";
import { cleanup } from "_src/reformer/property"
import v5 from "_src/reformer/v5/reformer"
import v6 from "_src/reformer/v6/reformer"
import common from "_src/reformer/common/reformer"
import projectTree from "_src/reformer/project_tree";

let _currentJson = null;
let _projectDirectory = null;
export default {
  initProjectStructure(json) {
    projectTree._cleanup();

    const version = json["version"]["format"];
    projectTree.version = version;

    const scanList = json["scans"] || [];
    const fusionList = json["fusions"]|| [];
    const cadList = json["cad-objects"]|| [];
    const pointCloudList = json["point-clouds"]|| [];
    const groupsList = json["groups"]|| [];
    const orderingList = json["ordering"]|| [];
    const linearMeasuresList = json["linear-measures"] || [];
    const geodesicMeasuresList = json["geodesic-measures"] || [];
    const sectionMeasuresList = json["section-measures"] || [];
    const distanceMapsMeasuresList = json["distance-maps"] || [];
    const photoGroupsList = json["photo-sets"] || [];
    const rawScansList = json["raw-scans"] || [];

    const existedColors = [];
    for (const object of fusionList.concat(scanList).concat(cadList).concat(pointCloudList)) {
      if (object["extra"] && object["extra"]["color"]) {
        existedColors.push(object["extra"]["color"]);
      }
    }

    idGenerator.initialize(json);

    const objColorsInGroups = {};

    for (const scanData of scanList) {
      const property = createObjectProperty(scanData, objectType.SCAN, version, existedColors);
      fixRawScansTargeting(property, rawScansList);
      projectTree.addProperty(property);
      storeShade(property, objColorsInGroups);
    }

    for (const pointCloudData of pointCloudList) {
      const property = createObjectProperty(pointCloudData, objectType.POINTCLOUD, version, existedColors);
      projectTree.addProperty(property);
      storeShade(property, objColorsInGroups);
    }

    for (const fusionData of fusionList) {
      const property = createObjectProperty(fusionData, objectType.FUSION, version, existedColors);
      projectTree.addProperty(property);
      storeShade(property, objColorsInGroups);
    }

    for (const cadData of cadList) {
      const property = createObjectProperty(cadData, objectType.CAD, version, existedColors);
      projectTree.addProperty(property);
      storeShade(property, objColorsInGroups);
    }

    for (const groupData of groupsList) {
      const property = createGroupProperty(groupData, objColorsInGroups);
      projectTree.addProperty(property);
    }

    for (const linearMeasureData of linearMeasuresList) {
      const property = createMeasureProperty(linearMeasureData, objectType.LINEAR_MEASURE);
      projectTree.addProperty(property);
    }

    for (const geodesicMeasureData of geodesicMeasuresList) {
      const property = createMeasureProperty(geodesicMeasureData, objectType.GEODESIC_MEASURE);
      projectTree.addProperty(property);
    }

    for (const sectionMeasureData of sectionMeasuresList) {
      const property = createSectionProperty(sectionMeasureData);
      projectTree.addProperty(property);
    }

    for (const distanceMapData of distanceMapsMeasuresList) {
      const property = createDistanceMapProperty(distanceMapData);
      projectTree.addProperty(property);
    }

    for (const photoGroupData of photoGroupsList) {
      const property = createPhotoGroupProperty(photoGroupData);
      projectTree.addProperty(property);
    }

    const ordering = [];
    for (const item of orderingList) {
      ordering.push(item ? { "id" : item["id"] } : null);
    }
    projectTree.setOrdering(ordering);

    _currentJson = json;
    events.emit("currentProjectJson", _currentJson);
  },

  setProjectPath(projectPath) {
    _projectDirectory = projectPath.substring(0, projectPath.lastIndexOf("/")) + "/";
  },

  getProjectDirectory() {
    return _projectDirectory;
  },

  getSerializedProject() {
    if (!_currentJson) {
      output.warn("Can't serialize the project. Missing original JSON");
      return null;
    }

    common.serializeObjects(_currentJson);
    common.serializeOrdering(_currentJson);

    return _currentJson;
  },

  async combineObjectStructure(property) {
    await v6.combineObjectStructure(property, _projectDirectory);
  },

  generateHexColorRandom() {
    return getHexPaletteRandom();
  },

  cleanup() {
    cleanup();
    projectTree._cleanup();
  },
}

function createMeasureProperty(data, type) {
  return v6.initMeasureProperty(data, type);
}

function createSectionProperty(data) {
  return v6.initSectionProperty(data);
}

function createDistanceMapProperty(data) {
  return v6.initDistanceMapProperty(data);
}

function createPhotoGroupProperty(data) {
  return v6.initPhotoGroupProperty(data);
}

function createObjectProperty(data, type, format, existedColors) {
  if (format <= 5) {
    return v5.initObjectProperty(data, type, existedColors);
  } else {
    return v6.initObjectProperty(data, type, existedColors);
  }
}

function createGroupProperty(data, objColorsInGroups) {
  return common.createGroupProperty(data, objColorsInGroups[data["id"]]);
}

function getHexPaletteRandom() {
  return colorUtils.getPaletteColorRandom();
}

function storeShade(object, storage) {
  const id = object.getValue("groupID");
  if (id) {
    if (!(id in storage)) {
      storage[id] = []
    }
    storage[id].push(colorUtils.hexToRgb(object.getValue("color")));
  }
}

function fixRawScansTargeting(scanProperty, rawScansList) {
  const targetRawScan = scanProperty.getValue("targetRawScan");
  if (targetRawScan !== scanProperty.getValue("uuid")) {
    if (!rawScansList.find(rawScan => rawScan["extra"]["uuid"] === targetRawScan)) {
      scanProperty.setValue("hasrawgeometry", false);
      scanProperty.setValue("targetRawScan", "fixed inconsistency");
    }
  }
}
